import React from "react";
import { Box, Typography } from "@mui/material";
import { AccessTime } from "@mui/icons-material";
import { MessageBubbleContainer, MessageContent } from "./SharedStyledComponents";

const PaymentOptionMessage = ({
  message,
  isOwn,
  currentUser,
  storedUserId,
  onOpenPaymentDialog,
  getReadReceiptStatus,
  getFormattedMessageText,
}) => {
  const orderId = message?.orderId;
  const paymentFlow = orderId?.paymentFlow;
  const paymentFlowStatus = paymentFlow?.status;
  const isPaymentInitiated =
    paymentFlowStatus === "pending_approval" ||
    paymentFlowStatus === "approved";

  const groupOrder = orderId?.groupOrder;
  const userOrders = groupOrder?.userOrders || [];
  const currentUserId = currentUser?.userId ?? storedUserId;
  const currentUserOrder = userOrders.find((userOrder) => {
    const userId = userOrder.userId?._id || userOrder.userId;
    return userId === currentUserId;
  });
  const hasUserChosenSplitPerPerson =
    currentUserOrder?.paymentType === "split_per_person";

  const upfrontPercentage = orderId?.paymentSettings?.upfrontPercentage || 0;
  const groupSubtotal = orderId?.groupSubtotal || 0;
  const requiredGroupUpfront = (groupSubtotal * upfrontPercentage) / 100;
  const totalPaidAmount = orderId?.totalPaidAmount || 0;
  const groupRemainingUpfront = Math.max(0, requiredGroupUpfront - totalPaidAmount);
  const hasRemainingUpfront = groupRemainingUpfront > 0.01;

  const hasUserPaymentTypeNull = currentUserOrder?.paymentType === null;

  const someoneElsePayingForAll = userOrders.some(
    (userOrder) =>
      userOrder.paymentType === "pay_for_all" &&
      (userOrder.userId?._id || userOrder.userId) !== currentUserId
  );

  const isPaymentCompleted = orderId?.paymentCompleted === true;

  const shouldDisableTipHere = isPaymentCompleted
    ? true
    : hasRemainingUpfront
    ? false
    : isPaymentInitiated &&
      (hasUserChosenSplitPerPerson ||
        !hasUserPaymentTypeNull ||
        someoneElsePayingForAll);

  const orderBusinessId = orderId?.businessId?._id || orderId?.businessId;
  const orderLocationId = orderId?.locationId?._id || orderId?.locationId;
  let isCurrentUserOwnerOrTeamMemberForThisOrder = false;

  const employeeInfo = currentUser?.employee;
  if (employeeInfo) {
    const employeeBusinessId =
      employeeInfo?.businessId?._id || employeeInfo?.businessId;
    const isSameBusiness =
      employeeBusinessId &&
      orderBusinessId &&
      employeeBusinessId === orderBusinessId;

    let hasSameLocation = false;
    if (isSameBusiness) {
      const employeeLocationIds = Array.isArray(employeeInfo?.locationIds)
        ? employeeInfo.locationIds
        : [];
      hasSameLocation = employeeLocationIds.some((loc) => {
        const locId = loc?._id || loc;
        return orderLocationId && locId === orderLocationId;
      });
    }

    isCurrentUserOwnerOrTeamMemberForThisOrder = Boolean(
      isSameBusiness || hasSameLocation
    );
  }

  if (isCurrentUserOwnerOrTeamMemberForThisOrder) {
    return null;
  }

  return (
    <MessageBubbleContainer isOwn={isOwn}>
      {!isOwn && (
        <Typography
          variant="caption"
          sx={{
            color: "#374151",
            fontSize: { xs: "0.75rem", sm: "0.8rem" },
            fontWeight: 600,
            mb: 0.5,
            alignSelf: "flex-start",
          }}
        >
          {message.senderId?.fullName || message.senderId?.userName || "user"}
        </Typography>
      )}

      <MessageContent
        isOwn={isOwn}
        isTemp={message.isTemp}
        isOrder={false}
        isPayment={true}
        isRequest={false}
        idDraft={false}
        isDisabled={false}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            width: "100%",
            minWidth: "280px",
            maxWidth: "380px",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: isOwn ? "#FFFFFF" : "#000000",
              fontSize: "15px",
              fontWeight: 400,
              lineHeight: 1.4,
            }}
          >
            {getFormattedMessageText() ||
              (upfrontPercentage > 0
                ? `thank you for your patronage! ${upfrontPercentage}% upfront has been requested for your order. please make payment or add a `
                : "thank you for your patronage! your bill is ready. please make payment or add a ")}
            <Box
              component="span"
              sx={{
                color: shouldDisableTipHere ? "#9CA3AF" : (isOwn ? "#FDE047" : "#7C3AED"),
                fontWeight: 700,
                textDecoration: shouldDisableTipHere ? "none" : "underline",
                cursor: shouldDisableTipHere ? "not-allowed" : "pointer",
                opacity: shouldDisableTipHere ? 0.6 : 1,
                "&:hover": {
                  color: shouldDisableTipHere ? "#9CA3AF" : (isOwn ? "#FEF08A" : "#6D28D9"),
                  textDecoration: shouldDisableTipHere ? "none" : "underline",
                },
              }}
              onClick={() => {
                if (shouldDisableTipHere) {
                  return;
                }
                onOpenPaymentDialog?.(message);
              }}
            >
              tip here.
            </Box>
          </Typography>
        </Box>
      </MessageContent>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mt: 0.5,
          alignSelf: isOwn ? "flex-end" : "flex-start",
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: "rgba(0,0,0,0.6)",
            fontSize: { xs: "0.7rem", sm: "0.75rem" },
          }}
        >
          {message.timestamp}
        </Typography>

        {isOwn && (
          <Typography
            variant="caption"
            sx={{
              color: "rgba(0,0,0,0.6)",
              fontSize: { xs: "0.7rem", sm: "0.75rem" },
            }}
          >
            {message.isTemp ? (
              <AccessTime fontSize="inherit" />
            ) : (
              getReadReceiptStatus() || "✓"
            )}
          </Typography>
        )}
      </Box>
    </MessageBubbleContainer>
  );
};

export default PaymentOptionMessage;
